/**
 * Eval Personas Management View
 *
 * CRUD page for managing eval personas. Shows system (CEFR) personas
 * as read-only and custom personas as editable/deletable.
 *
 * Story: 24.1 - Lab Foundation & Eval Personas
 */

import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  useEvalPersonas,
  useCreateEvalPersona,
  useUpdateEvalPersona,
  useDeleteEvalPersona,
  type EvalPersona,
} from '../services/personaApi';
import { EpisodeNavigation } from '../components/EpisodeNavigation';

interface PersonaFormData {
  name: string;
  description: string;
}

export function EvalPersonasView() {
  const { data: personas, isLoading } = useEvalPersonas();
  const createMutation = useCreateEvalPersona();
  const updateMutation = useUpdateEvalPersona();
  const deleteMutation = useDeleteEvalPersona();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState<EvalPersona | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EvalPersona | null>(null);

  const systemPersonas = personas?.filter((p) => p.isSystem) || [];
  const customPersonas = personas?.filter((p) => !p.isSystem) || [];

  const handleCreate = () => {
    setEditingPersona(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (persona: EvalPersona) => {
    setEditingPersona(persona);
    setIsDialogOpen(true);
  };

  const handleSave = async (formData: PersonaFormData) => {
    try {
      if (editingPersona) {
        await updateMutation.mutateAsync({
          id: editingPersona.id,
          ...formData,
        });
        toast.success('Persona updated');
      } else {
        await createMutation.mutateAsync(formData);
        toast.success('Persona created');
      }
      setIsDialogOpen(false);
    } catch (error) {
      const message =
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'data' in error.response &&
        error.response.data &&
        typeof error.response.data === 'object' &&
        'error' in error.response.data &&
        error.response.data.error &&
        typeof error.response.data.error === 'object' &&
        'message' in error.response.data.error
          ? String(error.response.data.error.message)
          : 'Failed to save persona';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Persona deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete persona');
    }
  };

  return (
    <div>
      <EpisodeNavigation />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Eval Personas</h1>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
        >
          Create Persona
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {!isLoading && personas && (
        <div className="space-y-8">
          {/* System Personas */}
          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">System (CEFR)</h2>
            <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
              {systemPersonas.map((persona) => (
                <PersonaRow
                  key={persona.id}
                  persona={persona}
                  onEdit={() => handleEdit(persona)}
                  onDelete={() => setDeleteTarget(persona)}
                />
              ))}
            </div>
          </section>

          {/* Custom Personas */}
          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Custom</h2>
            {customPersonas.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-500">
                No custom personas yet. Create one to test specific learner archetypes.
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
                {customPersonas.map((persona) => (
                  <PersonaRow
                    key={persona.id}
                    persona={persona}
                    onEdit={() => handleEdit(persona)}
                    onDelete={() => setDeleteTarget(persona)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* Create/Edit Dialog */}
      {isDialogOpen && (
        <PersonaDialog
          persona={editingPersona}
          onSave={handleSave}
          onClose={() => setIsDialogOpen(false)}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <DeleteConfirmDialog
          personaName={deleteTarget.name}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

// ============================================================================
// Persona Row Component
// ============================================================================

function PersonaRow({
  persona,
  onEdit,
  onDelete,
}: {
  persona: EvalPersona;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{persona.name}</span>
              {persona.cefrCode && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                  {persona.cefrCode}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1 truncate">
              {persona.description.length > 100
                ? `${persona.description.slice(0, 100)}...`
                : persona.description}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1"
          >
            {isExpanded ? 'Hide' : 'Show'}
          </button>
          <button
            onClick={onEdit}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium px-2 py-1"
          >
            Edit
          </button>
          <button
            onClick={onDelete}
            className="text-sm text-red-600 hover:text-red-800 font-medium px-2 py-1"
          >
            Delete
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{persona.description}</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Persona Dialog Component
// ============================================================================

function PersonaDialog({
  persona,
  onSave,
  onClose,
  isSaving,
}: {
  persona: EvalPersona | null;
  onSave: (data: PersonaFormData) => void;
  onClose: () => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState(persona?.name || '');
  const [description, setDescription] = useState(persona?.description || '');
  const [errors, setErrors] = useState<{ name?: string; description?: string }>({});

  const validate = (): boolean => {
    const newErrors: { name?: string; description?: string } = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (name.length > 100) newErrors.name = 'Name must be 100 characters or less';
    if (description.length < 20)
      newErrors.description = 'Description must be at least 20 characters';
    if (description.length > 2000)
      newErrors.description = 'Description must be 2000 characters or less';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave({ name: name.trim(), description: description.trim() });
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-bold text-gray-900">
            {persona ? 'Edit Persona' : 'Create Persona'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="persona-name" className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              id="persona-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Shy Teenager"
            />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label
              htmlFor="persona-description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Description (behavioral rules)
            </label>
            <textarea
              id="persona-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Write actionable behavioral rules for the simulated learner..."
            />
            <div className="flex justify-between mt-1">
              {errors.description ? (
                <p className="text-sm text-red-600">{errors.description}</p>
              ) : (
                <span />
              )}
              <span className="text-xs text-gray-400">{description.length}/2000</span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-400"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Delete Confirmation Dialog
// ============================================================================

function DeleteConfirmDialog({
  personaName,
  onConfirm,
  onCancel,
  isDeleting,
}: {
  personaName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}) {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Persona</h3>
        <p className="text-sm text-gray-600 mb-6">
          Are you sure you want to delete persona &ldquo;{personaName}&rdquo;?
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium disabled:bg-gray-400"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
